import type { Race, CharacterClass, Subrace, Background, FeatDefinition, Spell } from '@character-creator/types';
import { races } from './races';
import { classes } from './classes';
import { backgrounds } from './backgrounds';
import { rulesetRegistry } from './rulesets';
import { feats } from './feats';
import { spells } from './spells';

export { races } from './races';
export { classes } from './classes';
export { backgrounds } from './backgrounds';
export * from './schemas';
export * from './equipment';
export * from './spells';
export * from './conditions';
export * from './feats';
export * from './beasts';
export * from './fighting-styles';
export * from './class-features';
export * from './rulesets';
export * from './combat/combatTips';

// Lookup maps for efficient access
export const raceMap = new Map<string, Race>(races.map((r) => [r.id, r]));
export const classMap = new Map<string, CharacterClass>(classes.map((c) => [c.id, c]));
export const backgroundMap = new Map<string, Background>(backgrounds.map((b) => [b.id, b]));

// Helper functions

/**
 * Get a race by ID
 */
export function getRace(id: string): Race | undefined {
  return raceMap.get(id);
}

/**
 * Get all races, optionally filtered by source
 */
export function getRaces(source?: string): Race[] {
  return source ? races.filter((r) => r.source === source) : races;
}

/**
 * Get a class by ID
 */
export function getClass(id: string): CharacterClass | undefined {
  return classMap.get(id);
}

/**
 * Get all classes, optionally filtered by source
 */
export function getClasses(source?: string): CharacterClass[] {
  return source ? classes.filter((c) => c.source === source) : classes;
}

/**
 * Get a background by ID
 */
export function getBackground(id: string): Background | undefined {
  return backgroundMap.get(id);
}

/**
 * Get all backgrounds, optionally filtered by source
 */
export function getBackgrounds(source?: string): Background[] {
  return source ? backgrounds.filter((b) => b.source === source) : backgrounds;
}

export function getRaceWithSubrace(
  raceId: string,
  subraceId?: string
): { race: Race; subrace?: Subrace } | undefined {
  const race = raceMap.get(raceId);
  if (!race) return undefined;

  if (subraceId && race.subraces) {
    const subrace = race.subraces.find((s: Subrace) => s.id === subraceId);
    return { race, subrace };
  }

  return { race };
}

// =============================================================================
// Ruleset-Aware Data Getters
// =============================================================================
// These functions return data filtered by ruleset, supporting inheritance.
// A ruleset's `sources` array defines which data sources to include.
// Later sources can override earlier ones (e.g., Ignus can override SRD content).

/**
 * Generic dedup helper: single-pass filter + deduplicate by ID,
 * preferring later sources in the config's source list.
 */
function deduplicateForRuleset<T extends { id: string; source: string }>(
  items: T[],
  sources: string[],
): T[] {
  const sourceSet = new Set(sources);
  const sourceOrder = new Map(sources.map((s, i) => [s, i]));
  const byId = new Map<string, T>();
  const bestOrder = new Map<string, number>();

  for (const item of items) {
    if (!sourceSet.has(item.source)) continue;
    const order = sourceOrder.get(item.source)!;
    const existing = bestOrder.get(item.id);
    if (existing === undefined || order >= existing) {
      byId.set(item.id, item);
      bestOrder.set(item.id, order);
    }
  }

  return Array.from(byId.values());
}

// Module-level cache for ruleset data getters
const rulesetCache = {
  races: new Map<string, Race[]>(),
  classes: new Map<string, CharacterClass[]>(),
  backgrounds: new Map<string, Background[]>(),
  feats: new Map<string, FeatDefinition[]>(),
  spells: new Map<string, Spell[]>(),
};

/**
 * Get all races for a specific ruleset
 * Returns races from all sources defined in the ruleset config
 * Later sources override earlier sources (e.g., Ignus version of Dwarf replaces SRD Dwarf)
 */
export function getRacesForRuleset(rulesetId: string): Race[] {
  const config = rulesetRegistry[rulesetId];
  if (!config) return races;

  const cached = rulesetCache.races.get(rulesetId);
  if (cached) return cached;

  const result = deduplicateForRuleset(races, config.sources);
  rulesetCache.races.set(rulesetId, result);
  return result;
}

/**
 * Get a specific race for a ruleset, with override support
 * Checks sources in reverse order so later sources (e.g., ignus) override earlier (e.g., srd-5.1)
 */
export function getRaceForRuleset(id: string, rulesetId: string): Race | undefined {
  const config = rulesetRegistry[rulesetId];
  if (!config) return getRace(id);

  // Check sources in reverse order (later sources override earlier)
  for (const source of [...config.sources].reverse()) {
    const race = races.find((r) => r.id === id && r.source === source);
    if (race) return race;
  }
  return undefined;
}

/**
 * Get all classes for a specific ruleset
 * Returns classes from all sources defined in the ruleset config
 * Later sources override earlier sources (e.g., Ignus version replaces SRD version)
 * Subclasses within each class are deduplicated by name, preferring later sources
 */
export function getClassesForRuleset(rulesetId: string): CharacterClass[] {
  const config = rulesetRegistry[rulesetId];
  if (!config) return classes;

  const cached = rulesetCache.classes.get(rulesetId);
  if (cached) return cached;

  const dedupedClasses = deduplicateForRuleset(classes, config.sources);

  // Deduplicate subclasses by name within each class, preferring later sources
  const result = dedupedClasses.map((cls) => {
    if (!cls.subclasses || cls.subclasses.length === 0) return cls;

    const subclassByName = new Map<string, typeof cls.subclasses[0]>();
    for (const subclass of cls.subclasses) {
      if (!config.sources.includes(subclass.source)) continue;
      const existing = subclassByName.get(subclass.name);
      if (!existing || config.sources.indexOf(subclass.source) >= config.sources.indexOf(existing.source)) {
        subclassByName.set(subclass.name, subclass);
      }
    }

    return { ...cls, subclasses: Array.from(subclassByName.values()) };
  });

  rulesetCache.classes.set(rulesetId, result);
  return result;
}

/**
 * Get a specific class for a ruleset, with override support
 * Subclasses are deduplicated by name, preferring later sources
 */
export function getClassForRuleset(id: string, rulesetId: string): CharacterClass | undefined {
  const config = rulesetRegistry[rulesetId];
  if (!config) return getClass(id);

  // Find the class from the latest source
  let foundClass: CharacterClass | undefined;
  for (const source of [...config.sources].reverse()) {
    const cls = classes.find((c) => c.id === id && c.source === source);
    if (cls) {
      foundClass = cls;
      break;
    }
  }

  if (!foundClass) return undefined;

  // Deduplicate subclasses by name, preferring later sources
  if (!foundClass.subclasses || foundClass.subclasses.length === 0) return foundClass;

  const subclassByName = new Map<string, typeof foundClass.subclasses[0]>();
  for (const source of config.sources) {
    for (const subclass of foundClass.subclasses.filter((sc) => sc.source === source)) {
      subclassByName.set(subclass.name, subclass); // Later sources overwrite earlier ones
    }
  }

  return {
    ...foundClass,
    subclasses: Array.from(subclassByName.values()),
  };
}

/**
 * Get all backgrounds for a specific ruleset
 * Returns backgrounds from all sources defined in the ruleset config
 * Later sources override earlier sources (e.g., Ignus version replaces SRD version)
 */
export function getBackgroundsForRuleset(rulesetId: string): Background[] {
  const config = rulesetRegistry[rulesetId];
  if (!config) return backgrounds;

  const cached = rulesetCache.backgrounds.get(rulesetId);
  if (cached) return cached;

  const result = deduplicateForRuleset(backgrounds, config.sources);
  rulesetCache.backgrounds.set(rulesetId, result);
  return result;
}

/**
 * Get a specific background for a ruleset, with override support
 */
export function getBackgroundForRuleset(id: string, rulesetId: string): Background | undefined {
  const config = rulesetRegistry[rulesetId];
  if (!config) return getBackground(id);

  for (const source of [...config.sources].reverse()) {
    const bg = backgrounds.find((b) => b.id === id && b.source === source);
    if (bg) return bg;
  }
  return undefined;
}

/**
 * Get all feats for a specific ruleset
 * Returns feats from all sources defined in the ruleset config
 * Later sources override earlier sources (e.g., Ignus version of Grappler replaces SRD Grappler)
 */
export function getFeatsForRuleset(rulesetId: string): FeatDefinition[] {
  const config = rulesetRegistry[rulesetId];
  if (!config) return feats;

  const cached = rulesetCache.feats.get(rulesetId);
  if (cached) return cached;

  const result = deduplicateForRuleset(feats, config.sources);
  rulesetCache.feats.set(rulesetId, result);
  return result;
}

/**
 * Get all spells for a specific ruleset
 * Returns spells from all sources defined in the ruleset config
 * Later sources override earlier sources (e.g., Ignus version replaces SRD version)
 */
export function getSpellsForRuleset(rulesetId: string): Spell[] {
  const config = rulesetRegistry[rulesetId];
  if (!config) return spells;

  const cached = rulesetCache.spells.get(rulesetId);
  if (cached) return cached;

  const result = deduplicateForRuleset(spells, config.sources);
  rulesetCache.spells.set(rulesetId, result);
  return result;
}

/**
 * Get a race with subrace for a specific ruleset
 */
export function getRaceWithSubraceForRuleset(
  raceId: string,
  subraceId: string | null,
  rulesetId: string
): { race: Race; subrace?: Subrace } | undefined {
  const race = getRaceForRuleset(raceId, rulesetId);
  if (!race) return undefined;

  if (subraceId && race.subraces) {
    const subrace = race.subraces.find((s: Subrace) => s.id === subraceId);
    return { race, subrace };
  }

  return { race };
}
